import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import { BarChart3 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const StrategyAssessment = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [assessmentData, setAssessmentData] = useState({
    throughput_time_days: 0,
    bottleneck_locations: [],
    bottleneck_types: [],
    team_involvement_level: 3,
    flexibility_vs_standardization: 5,
    high_mix_low_volume_suitability: 5,
    digital_readiness: 5,
    cultural_fit: 5,
    implementation_effort_resources: 5,
    implementation_effort_time: 5,
    implementation_effort_skills: 5,
    implementation_effort_budget: 5,
    roi_expectations: 5
  });

  const handleSliderChange = (field, value) => {
    setAssessmentData(prev => ({
      ...prev,
      [field]: value[0]
    }));
  };

  const handleMultiSelectChange = (field, option, checked) => {
    setAssessmentData(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], option]
        : prev[field].filter(item => item !== option)
    }));
  };

  const submitAssessment = async () => {
    try {
      const userId = localStorage.getItem('userId');
      const response = await axios.post(`${API}/assessments`, {
        ...assessmentData,
        user_id: userId
      });
      localStorage.setItem('assessmentId', response.data.id);
      navigate('/recommendations');
    } catch (error) {
      console.error('Failed to submit assessment:', error);
    }
  };

  const bottleneckLocations = [
    'Machine shop', 'Assembly line', 'Quality control', 'Material handling',
    'Packaging', 'Shipping', 'Planning', 'Engineering'
  ];

  const bottleneckTypes = [
    'Equipment capacity', 'Skilled labor shortage', 'Material supply',
    'Information flow', 'Quality issues', 'Setup times', 'Batch processing'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-12">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Manufacturing Assessment</h1>
            <p className="text-xl text-gray-600">Help us understand your current situation to provide the best recommendations</p>
          </div>

          <Card className="p-8 bg-white shadow-2xl">
            <div className="space-y-8">
              <div>
                <Label className="text-lg font-semibold mb-4 block">Current Average Throughput Time</Label>
                <div className="flex items-center space-x-4">
                  <Input
                    type="number"
                    value={assessmentData.throughput_time_days}
                    onChange={(e) => setAssessmentData(prev => ({...prev, throughput_time_days: parseFloat(e.target.value) || 0}))}
                    placeholder="0"
                    className="w-32"
                  />
                  <span className="text-gray-600">days</span>
                </div>
              </div>

              <div>
                <Label className="text-lg font-semibold mb-4 block">Where are your main bottlenecks located?</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {bottleneckLocations.map((location) => (
                    <div key={location} className="flex items-center space-x-3">
                      <Checkbox
                        id={location}
                        checked={assessmentData.bottleneck_locations.includes(location)}
                        onCheckedChange={(checked) => handleMultiSelectChange('bottleneck_locations', location, checked)}
                      />
                      <Label htmlFor={location} className="text-sm">{location}</Label>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-lg font-semibold mb-4 block">What types of bottlenecks do you experience?</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {bottleneckTypes.map((type) => (
                    <div key={type} className="flex items-center space-x-3">
                      <Checkbox
                        id={type}
                        checked={assessmentData.bottleneck_types.includes(type)}
                        onCheckedChange={(checked) => handleMultiSelectChange('bottleneck_types', type, checked)}
                      />
                      <Label htmlFor={type} className="text-sm">{type}</Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Slider Assessments */}
              <div className="space-y-6">
                <div>
                  <Label className="text-lg font-semibold mb-4 block">
                    Team Involvement Level (1 = Low, 5 = High)
                  </Label>
                  <Slider
                    value={[assessmentData.team_involvement_level]}
                    onValueChange={(value) => handleSliderChange('team_involvement_level', value)}
                    max={5}
                    min={1}
                    step={1}
                    className="w-full"
                  />
                  <div className="text-center mt-2 text-sm text-gray-600">
                    Current: {assessmentData.team_involvement_level}
                  </div>
                </div>

                <div>
                  <Label className="text-lg font-semibold mb-4 block">
                    Need for Flexibility vs Standardization (1 = High Standardization, 10 = High Flexibility)
                  </Label>
                  <Slider
                    value={[assessmentData.flexibility_vs_standardization]}
                    onValueChange={(value) => handleSliderChange('flexibility_vs_standardization', value)}
                    max={10}
                    min={1}
                    step={1}
                    className="w-full"
                  />
                  <div className="text-center mt-2 text-sm text-gray-600">
                    Current: {assessmentData.flexibility_vs_standardization}
                  </div>
                </div>

                <div>
                  <Label className="text-lg font-semibold mb-4 block">
                    Digital Readiness (1 = Low, 10 = High)
                  </Label>
                  <Slider
                    value={[assessmentData.digital_readiness]}
                    onValueChange={(value) => handleSliderChange('digital_readiness', value)}
                    max={10}
                    min={1}
                    step={1}
                    className="w-full"
                  />
                  <div className="text-center mt-2 text-sm text-gray-600">
                    Current: {assessmentData.digital_readiness}
                  </div>
                </div>

                <div>
                  <Label className="text-lg font-semibold mb-4 block">
                    ROI Expectations (1 = Low, 10 = High)
                  </Label>
                  <Slider
                    value={[assessmentData.roi_expectations]}
                    onValueChange={(value) => handleSliderChange('roi_expectations', value)}
                    max={10}
                    min={1}
                    step={1}
                    className="w-full"
                  />
                  <div className="text-center mt-2 text-sm text-gray-600">
                    Current: {assessmentData.roi_expectations}
                  </div>
                </div>
              </div>

              <div className="flex justify-center">
                <Button
                  onClick={submitAssessment}
                  size="lg"
                  className="px-12 py-6 text-lg font-semibold bg-blue-600 hover:bg-blue-700"
                >
                  <BarChart3 className="mr-3 h-6 w-6" />
                  Get My Strategy Recommendations
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default StrategyAssessment;
